/**
 * Oat Cult cart drawer upsells (<oc-cart-upsells>), rendered by snippets/cart-upsells.liquid, plus the
 * drawer's quantity handling. Every cart request asks Shopify to return the drawer section too, and the
 * response is handed to Impact's own `cart:change` pipeline (see `publish`), so the drawer, the cart count
 * and Impact's cached cart all update from one round trip.
 *   - data-oc-switch: JSON array of {key, quantity, plan}; moves each line onto that selling plan.
 *   - data-oc-add-card: a variant id; adds one Cult Card to the cart.
 *   - data-oc-add-pack / data-oc-set-packs: grow the box to a ladder tier.
 */
(function () {
  if (customElements.get('oc-cart-upsells')) {
    return;
  }

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function post(path, body) {
    return fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (json) {
        if (!response.ok) {
          throw new Error(json.description || json.message || 'Cart request failed');
        }
        return json;
      });
    });
  }

  /** Impact's section id for the drawer (`shopify-section-<id>` wrapper), when the drawer is on the page. */
  function drawerSectionId() {
    var drawer = document.getElementById('cart-drawer');
    var wrapper = drawer ? drawer.closest('.shopify-section') : null;
    return wrapper ? wrapper.id.replace('shopify-section-', '') : '';
  }

  /** A cart mutation that also brings back the rendered drawer, so no second fetch is needed to redraw. */
  function change(path, body) {
    var sectionId = drawerSectionId();
    if (sectionId) {
      body.sections = [sectionId];
    }
    return post(path, body);
  }

  /**
   * Hand a cart response to Impact. With the drawer section in it, `cart:change` redraws the drawer at once
   * (Impact only delays the redraw for its own line edits), updates <cart-count> and refreshes its cart cache.
   * Without a section (older response, failure) fall back to a full `cart:refresh`.
   */
  function publish(cart) {
    var sectionId = drawerSectionId();
    if (cart && cart.sections && sectionId && cart.sections[sectionId]) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', ocSource: true, cart: cart }
      }));
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  class OcCartUpsells extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this.addEventListener('click', this._onClick.bind(this), { signal: this._abortController.signal });
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
      }
    }

    async _onClick(event) {
      var button = event.target.closest('[data-oc-switch], [data-oc-add-card], [data-oc-add-pack], [data-oc-set-packs]');
      if (!button || button.disabled || !this.contains(button)) {
        return;
      }
      var errorEl = button.parentElement ? button.parentElement.querySelector('[data-oc-error]') : null;
      var label = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = button.getAttribute('data-loading') || label;
      if (errorEl) {
        errorEl.hidden = true;
      }

      try {
        var cart = null;
        if (button.hasAttribute('data-oc-switch')) {
          var lines = JSON.parse(button.getAttribute('data-oc-switch'));
          for (var i = 0; i < lines.length; i++) {
            cart = await change('cart/change.js', { id: lines[i].key, quantity: lines[i].quantity, selling_plan: lines[i].plan });
          }
        } else if (button.hasAttribute('data-oc-add-pack')) {
          var pack = JSON.parse(button.getAttribute('data-oc-add-pack'));
          cart = await change('cart/change.js', { id: pack.key, quantity: pack.quantity, selling_plan: pack.plan });
          cart = (await retier(cart)) || cart;
        } else if (button.hasAttribute('data-oc-set-packs')) {
          cart = await setPacks(Number(button.getAttribute('data-oc-set-packs')));
        } else {
          await post('cart/add.js', { items: [{ id: Number(button.getAttribute('data-oc-add-card')), quantity: 1 }] });
          cart = await change('cart/update.js', { updates: {} });
        }
        publish(cart);
        maybeRite(cart);
      } catch (error) {
        console.warn('[cart-upsells]', error);
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = label;
        if (errorEl) {
          errorEl.hidden = false;
        }
      }
    }
  }

  customElements.define('oc-cart-upsells', OcCartUpsells);

  /* ---------------------------------------------------------------- ladder re-tiering */

  /** Packs in a line: the variant title is the sachet count (9 / 18 / 27). */
  function packsOf(item) {
    var sachets = parseInt(String(item.variant_title || '').replace(/pack/i, ''), 10) || 9;
    return Math.max(1, Math.round(sachets / 9)) * item.quantity;
  }

  /** Plan maps rendered on the line selects, keyed by variant id (stable when a line's key changes). */
  function planMaps() {
    var maps = {};
    document.querySelectorAll('.oc-line-plan[data-oc-variant]').forEach(function (select) {
      try {
        maps[select.getAttribute('data-oc-variant')] = JSON.parse(select.getAttribute('data-oc-plans') || '{}');
      } catch (error) {
        /* ignore a malformed map */
      }
    });
    return maps;
  }

  var retierQueue = Promise.resolve();
  var retierWaiting = 0;

  /**
   * Keep every subscription oat line on the ladder plan for the box's total pack count
   * (1 pack 10%, 2 packs 15%, 3+ packs 20%), preserving each line's cadence. Runs one at a time; `cart` can
   * be a response the caller just received (only trusted when nothing else is queued). Resolves to the latest
   * cart response when a line changed, otherwise null.
   */
  function retier(cart) {
    var known = retierWaiting === 0 ? cart : null;
    retierWaiting += 1;
    var run = retierQueue.then(function () { return retierNow(known); });
    retierQueue = run.then(function (result) {
      retierWaiting -= 1;
      return result;
    }, function () {
      retierWaiting -= 1;
      return null;
    });
    return retierQueue;
  }

  async function retierNow(cart) {
    try {
      var maps = planMaps();
      if (!cart || !cart.items) {
        cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      }
      var subLines = (cart.items || []).filter(function (item) {
        return item.selling_plan_allocation && maps[String(item.variant_id)];
      });
      var packs = subLines.reduce(function (sum, item) { return sum + packsOf(item); }, 0);
      if (!packs) {
        return null;
      }
      var pct = tierPct(packs);
      var latest = null;
      for (var i = 0; i < subLines.length; i++) {
        var line = subLines[i];
        var name = String(line.selling_plan_allocation.selling_plan.name || '').toLowerCase();
        if (name.indexOf('subscribe') === -1 || name.indexOf(pct + '% off') !== -1) {
          continue;
        }
        var target = maps[String(line.variant_id)][pct + '_' + cadenceOf(line)];
        if (!target) {
          continue;
        }
        latest = await change('cart/change.js', { id: line.key, quantity: line.quantity, selling_plan: target });
        rekeyLine(line.key, line.variant_id, target, latest);
      }
      return latest;
    } catch (error) {
      console.warn('[cart-upsells] retier skipped', error);
      return null;
    }
  }

  function tierPct(packs) {
    return packs === 1 ? 10 : packs === 2 ? 15 : 20;
  }

  function cadenceOf(item) {
    var name = item.selling_plan_allocation ? String(item.selling_plan_allocation.selling_plan.name || '').toLowerCase() : '';
    return name.indexOf('4 week') !== -1 ? '4' : '6';
  }

  /** A line moved onto another plan gets a new key: point the drawer's controls for the old key at it. */
  function rekeyLine(oldKey, variantId, planId, cart, extra) {
    var moved = (cart && cart.items ? cart.items : []).filter(function (item) {
      return String(item.variant_id) === String(variantId) && item.selling_plan_allocation && String(item.selling_plan_allocation.selling_plan.id) === String(planId);
    })[0];
    if (!moved || moved.key === oldKey) {
      return;
    }
    var targets = Array.prototype.slice.call(document.querySelectorAll('#cart-drawer [data-line-key], #cart-drawer [data-oc-line]'));
    (extra || []).forEach(function (el) {
      if (el && targets.indexOf(el) === -1) {
        targets.push(el);
      }
    });
    targets.forEach(function (el) {
      if (el.getAttribute('data-line-key') === oldKey) {
        el.setAttribute('data-line-key', moved.key);
      }
      if (el.getAttribute('data-oc-line') === oldKey) {
        el.setAttribute('data-oc-line', moved.key);
      }
    });
  }

  /**
   * Pack ladder tap: grow the box to `target` packs on the first 9-pack line, then put every oat line
   * (one-time lines included) on the ladder plan for that size. Cadence follows the line, else 6 weeks.
   */
  async function setPacks(target) {
    var maps = planMaps();
    var cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
    var oatLines = (cart.items || []).filter(function (item) { return maps[String(item.variant_id)]; });
    if (!oatLines.length) {
      return cart;
    }
    var packs = oatLines.reduce(function (sum, item) { return sum + packsOf(item); }, 0);
    var delta = target - packs;
    var pct = tierPct(target);
    if (delta > 0) {
      var line = oatLines.filter(function (item) { return packsOf(item) === item.quantity; })[0] || oatLines[0];
      var perUnit = packsOf(line) / line.quantity;
      var plan = maps[String(line.variant_id)][pct + '_' + cadenceOf(line)];
      cart = await change('cart/change.js', {
        id: line.key,
        quantity: line.quantity + Math.max(1, Math.ceil(delta / perUnit)),
        selling_plan: plan || (line.selling_plan_allocation ? line.selling_plan_allocation.selling_plan.id : '')
      });
    }
    // Everything else onto the same tier (this is also what turns a one-time box into a subscription).
    var rest = (cart.items || []).filter(function (item) { return maps[String(item.variant_id)]; });
    for (var i = 0; i < rest.length; i++) {
      var item = rest[i];
      var name = item.selling_plan_allocation ? String(item.selling_plan_allocation.selling_plan.name || '').toLowerCase() : '';
      if (name.indexOf('subscribe') !== -1 && name.indexOf(pct + '% off') !== -1) {
        continue;
      }
      var targetPlan = maps[String(item.variant_id)][pct + '_' + cadenceOf(item)];
      if (targetPlan) {
        cart = await change('cart/change.js', { id: item.key, quantity: item.quantity, selling_plan: targetPlan });
      }
    }
    return cart;
  }

  /* Per-line purchase option: subscribe at the tier's plan (6 or 4 weeks) or buy once. */
  document.addEventListener('change', async function (event) {
    var select = event.target && event.target.closest ? event.target.closest('.oc-line-plan') : null;
    if (!select) {
      return;
    }
    select.disabled = true;
    try {
      var value = select.value;
      var cart = await change('cart/change.js', {
        id: select.getAttribute('data-oc-line'),
        quantity: Number(select.getAttribute('data-oc-qty')) || 1,
        selling_plan: value === '' ? '' : Number(value)
      });
      publish((await retier(cart)) || cart);
    } catch (error) {
      console.warn('[cart-upsells] plan change failed', error);
      select.disabled = false;
    }
  });

  /* ---------------------------------------------------------------- instant ladder */

  function fill(template, tokens) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(tokens, key) ? String(tokens[key]) : match;
    });
  }

  /** Packs in the drawer, read from the line controls (ahead of the server), with one line's quantity overridden. */
  function packsInDrawer(overrideKey, overrideQty) {
    var packs = 0;
    var subPacks = 0;
    document.querySelectorAll('#cart-drawer .oc-line-plan[data-oc-variant]').forEach(function (select) {
      var key = select.getAttribute('data-oc-line');
      var qty = key === overrideKey ? overrideQty : (Number(select.getAttribute('data-oc-qty')) || 0);
      var sachets = parseInt(String(select.getAttribute('data-oc-sachets') || '9'), 10) || 9;
      var linePacks = Math.max(1, Math.round(sachets / 9)) * qty;
      packs += linePacks;
      if (select.value !== '') {
        subPacks += linePacks;
      }
    });
    return { packs: packs, subPacks: subPacks };
  }

  /** Redraw the ladder for a pack count without waiting for the server (mirrors snippets/cart-upsells.liquid). */
  function renderLadder(packs, isSub) {
    var holder = document.querySelector('#cart-drawer [data-oc-ladder]');
    var tpl = holder ? holder.closest('oc-cart-upsells') : null;
    if (!holder || !tpl) {
      return;
    }
    holder.hidden = packs <= 0;
    if (packs <= 0) {
      return;
    }
    var tier = Math.min(3, packs);
    var pct = tierPct(tier);
    var free = tpl.getAttribute('data-tpl-free') || '';
    var cta = holder.querySelector('.oc-ladder__cta');
    var text = '';
    var target = 0;
    if (isSub) {
      if (tier < 3) {
        var next = tier + 1;
        text = fill(tpl.getAttribute('data-tpl-add'), { ordinal: next === 2 ? '2nd' : '3rd', pct: tierPct(next), delivery: free });
        target = next;
      } else {
        text = fill(tpl.getAttribute('data-tpl-max'), { pct: pct });
      }
    } else if (tier <= 1) {
      text = fill(tpl.getAttribute('data-tpl-otp-grow'), { n: 2, pct: 15, delivery: free });
      target = 2;
    } else {
      text = fill(tpl.getAttribute('data-tpl-otp'), { pct: pct, delivery: free });
      target = packs;
    }
    if (cta) {
      cta.textContent = text;
      cta.classList.toggle('is-max', target === 0);
      cta.disabled = target === 0;
      cta.removeAttribute('aria-busy');
      if (target > 0) {
        cta.setAttribute('data-oc-set-packs', String(target));
      } else {
        cta.removeAttribute('data-oc-set-packs');
      }
    }
    holder.querySelectorAll('.oc-ladder__seg').forEach(function (seg) {
      var reached = Number(seg.getAttribute('data-oc-tier')) <= tier;
      seg.classList.toggle('is-on', isSub && reached);
      seg.classList.toggle('is-otp', !isSub && reached);
    });
    holder.querySelectorAll('.oc-ladder__label').forEach(function (label) {
      var reached = Number(label.getAttribute('data-oc-tier')) <= tier;
      var on = isSub && reached;
      label.classList.toggle('is-on', on);
      label.classList.toggle('is-otp', !isSub && reached);
      if (on) {
        label.setAttribute('aria-current', 'true');
        label.setAttribute('tabindex', '-1');
      } else {
        label.removeAttribute('aria-current');
        label.removeAttribute('tabindex');
      }
    });
  }

  /* ---------------------------------------------------------------- instant prices and totals */

  function formatMoney(cents) {
    var format = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || '£{{amount}}';
    var amount = (cents / 100).toFixed(2);
    var whole = String(Math.round(cents / 100));
    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
      .replace(/\{\{\s*amount\s*\}\}/g, amount)
      .replace(/<[^>]*>/g, '')
      .trim();
  }

  /** Replace the visible amount in a price element, keeping its screen-reader label span intact. */
  function setAmount(el, text) {
    var nodes = Array.prototype.filter.call(el.childNodes, function (node) {
      return node.nodeType === Node.TEXT_NODE && node.nodeValue.trim() !== '';
    });
    if (nodes.length) {
      nodes[nodes.length - 1].nodeValue = text;
    } else {
      el.textContent = text;
    }
  }

  /**
   * Redraw the oat lines' prices and the footer totals from the drawer's own data, ahead of the server.
   * Returns false when discounts are in play (only the server knows those), so the caller dims the numbers instead.
   */
  function renderTotals() {
    var totals = document.querySelector('#cart-drawer [data-oc-totals]');
    if (!totals || totals.hasAttribute('data-oc-discounted')) {
      return false;
    }
    var selects = document.querySelectorAll('#cart-drawer .oc-line-plan[data-oc-variant]');
    var allPacks = 0;
    var subPacks = 0;
    selects.forEach(function (select) {
      var sachets = parseInt(select.getAttribute('data-oc-sachets') || '9', 10) || 9;
      var packs = Math.max(1, Math.round(sachets / 9)) * (Number(select.getAttribute('data-oc-qty')) || 0);
      allPacks += packs;
      if (select.value !== '') {
        subPacks += packs;
      }
    });
    var subPct = tierPct(Math.max(1, subPacks));
    var labelPct = tierPct(Math.max(1, allPacks));
    var hasCard = totals.hasAttribute('data-oc-has-card');
    var cardPct = Number(totals.getAttribute('data-oc-card-pct')) || 0;
    var full = 0;
    var lines = 0;
    var savings = 0;
    var subTotal = Number(totals.getAttribute('data-oc-other-sub')) || 0;
    var otpTotal = Number(totals.getAttribute('data-oc-other-otp')) || 0;
    selects.forEach(function (select) {
      var qty = Number(select.getAttribute('data-oc-qty')) || 0;
      var unit = Number(select.getAttribute('data-oc-unit')) || 0;
      var isSub = select.value !== '';
      var planUnit = unit;
      if (isSub) {
        planUnit = Math.round(unit * (100 - subPct) / 100);
      } else if (hasCard && cardPct) {
        planUnit = Math.round(unit * (100 - cardPct) / 100);
      }
      var line = planUnit * qty;
      full += unit * qty;
      lines += line;
      // Plan savings on subscriptions, Cult Card savings on one-time oats: both belong in the savings row.
      savings += (unit - planUnit) * qty;
      if (isSub) {
        subTotal += line;
      } else {
        otpTotal += line;
      }
      var lineItem = select.closest('line-item');
      if (!lineItem || qty === 0) {
        return;
      }
      var price = lineItem.querySelector('.price-list sale-price');
      if (price) {
        setAmount(price, formatMoney(line));
      }
      var compare = lineItem.querySelector('.price-list compare-at-price');
      if (compare) {
        setAmount(compare, formatMoney(unit * qty));
      }
      if (!isSub && hasCard && price && line < unit * qty) {
        // A holder's discount only lands at checkout, so the drawer shows the card price with the full price struck through.
        if (!compare) {
          compare = document.createElement('compare-at-price');
          compare.className = 'text-subdued line-through' + (price.classList.contains('text-sm') ? ' text-sm' : '');
          price.insertAdjacentElement('afterend', compare);
        }
        price.classList.add('text-on-sale');
        price.classList.remove('text-subdued');
        setAmount(compare, formatMoney(unit * qty));
      }
      var saving = lineItem.querySelector('.oc-line-saving[data-oc-tpl]');
      if (saving) {
        saving.textContent = fill(saving.getAttribute('data-oc-tpl'), { amount: formatMoney((unit - planUnit) * qty) });
      }
      Array.prototype.forEach.call(select.options, function (option) {
        if (option.getAttribute('data-oc-tpl')) {
          option.textContent = fill(option.getAttribute('data-oc-tpl'), { pct: isSub ? subPct : labelPct });
        }
      });
    });
    var subtotalEl = totals.querySelector('[data-oc-subtotal]');
    var savingEl = totals.querySelector('[data-oc-saving]');
    var totalEl = document.querySelector('#cart-drawer [data-oc-total]');
    var others = (Number(totals.getAttribute('data-oc-other-sub')) || 0) + (Number(totals.getAttribute('data-oc-other-otp')) || 0);
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(full + others);
    }
    if (savingEl) {
      savingEl.textContent = '-' + formatMoney(savings);
    }
    if (totalEl) {
      totalEl.textContent = formatMoney(lines + others);
    }
    var delivery = totals.querySelector('.oc-delivery');
    if (delivery) {
      var free = hasCard || subTotal >= (Number(totals.getAttribute('data-oc-sub-min')) || 0) || otpTotal >= (Number(totals.getAttribute('data-oc-otp-min')) || 0);
      delivery.classList.toggle('is-free', free);
      var deliveryText = delivery.querySelector('.oc-delivery__text') || delivery;
      deliveryText.textContent = free ? totals.getAttribute('data-oc-free-text') : (subTotal > 0 ? totals.getAttribute('data-oc-paid-sub-text') : totals.getAttribute('data-oc-paid-otp-text'));
    }
    return true;
  }

  /* ---------------------------------------------------------------- line quantities */

  /*
   * Quantity edits in the drawer are handled here instead of by Impact's <line-item-quantity>: the stepper and
   * the ladder update the moment a button is tapped, quick taps collapse into one request that already carries
   * the ladder plan for the new box size, the other lines are re-tiered from that response, and the drawer is
   * redrawn once when the line is settled. Taps during a request are never dropped: they queue a follow-up.
   */
  var lineQueues = new WeakMap();
  var activeLines = new Set();

  function queueFor(selector) {
    var state = lineQueues.get(selector);
    if (!state) {
      state = { inFlight: false, dirty: false, timer: null, value: null };
      lineQueues.set(selector, state);
    }
    return state;
  }

  function lineSelect(key) {
    var selects = document.querySelectorAll('#cart-drawer .oc-line-plan[data-oc-line]');
    for (var i = 0; i < selects.length; i++) {
      if (selects[i].getAttribute('data-oc-line') === key) {
        return selects[i];
      }
    }
    return null;
  }

  /** Show the new quantity and ladder at once, then send the change after a short pause so taps coalesce. */
  function stageQuantity(selector, input, next) {
    var key = input.getAttribute('data-line-key');
    var state = queueFor(selector);
    input.value = String(next);
    var select = lineSelect(key);
    if (select) {
      select.setAttribute('data-oc-qty', String(next));
    }
    var counts = packsInDrawer(key, next);
    var isSub = counts.subPacks > 0;
    renderLadder(isSub ? counts.subPacks : counts.packs, isSub);
    state.value = next;
    activeLines.add(selector);
    markUpdating(!renderTotals());
    if (!selector.hasAttribute('aria-busy')) {
      selector.setAttribute('aria-busy', 'true');
      var lineItem = selector.closest('line-item');
      if (lineItem && lineItem.querySelector('pill-loader')) {
        lineItem.dispatchEvent(new CustomEvent('line-item:will-change', { bubbles: true, detail: { targetQuantity: next } }));
      }
    }
    clearTimeout(state.timer);
    state.timer = setTimeout(function () { flushQuantity(selector, input); }, next === 0 ? 0 : 180);
  }

  async function flushQuantity(selector, input) {
    var state = queueFor(selector);
    if (state.inFlight) {
      state.dirty = true;
      return;
    }
    state.inFlight = true;
    state.dirty = false;
    var key = input.getAttribute('data-line-key');
    var quantity = state.value;
    var select = lineSelect(key);
    var lineItem = selector.closest('line-item');
    var body = { id: key, quantity: quantity };
    if (quantity > 0 && select && select.value !== '') {
      var counts = packsInDrawer(key, quantity);
      var map = planMaps()[select.getAttribute('data-oc-variant')] || {};
      var option = select.options[select.selectedIndex];
      var cadence = String(option ? option.textContent : '').toLowerCase().indexOf('4 week') !== -1 ? '4' : '6';
      var plan = map[tierPct(counts.subPacks) + '_' + cadence];
      if (plan) {
        body.selling_plan = plan;
      }
    }
    var cart = null;
    try {
      cart = await change('cart/change.js', body);
      if (body.selling_plan && select) {
        rekeyLine(key, select.getAttribute('data-oc-variant'), body.selling_plan, cart, [input, select]);
      }
      cart = (await retier(cart)) || cart;
    } catch (error) {
      console.warn('[cart-upsells] quantity change failed', error);
      state.dirty = false;
      if (lineItem) {
        lineItem.removeAttribute('data-oc-removing');
      }
    } finally {
      state.inFlight = false;
    }
    if (state.dirty || state.value !== quantity) {
      clearTimeout(state.timer);
      flushQuantity(selector, input);
      return;
    }
    activeLines.delete(selector);
    selector.removeAttribute('aria-busy');
    markUpdating(false);
    if (lineItem && lineItem.querySelector('pill-loader')) {
      lineItem.dispatchEvent(new CustomEvent('line-item:change', { bubbles: true, detail: { quantity: quantity, cart: cart || { item_count: 1 } } }));
    }
    if (activeLines.size === 0) {
      publish(cart);
      maybeRite(cart);
    }
  }

  /** Dim the totals while a quantity is settling and they could not be computed client-side. */
  function markUpdating(dim) {
    var drawer = document.getElementById('cart-drawer');
    if (drawer) {
      drawer.classList.toggle('is-oc-updating', dim && activeLines.size > 0);
    }
  }

  function clampQuantity(input, next) {
    var max = parseInt(input.getAttribute('max') || '', 10);
    if (max > 0) {
      next = Math.min(next, max);
    }
    return Math.max(0, next);
  }

  /* Stepper buttons: handled before Impact's own listeners (capture phase) so each tap counts once. */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) {
      return;
    }
    var button = target.closest('#cart-drawer .quantity-selector--line .quantity-selector__button');
    if (button) {
      event.preventDefault();
      event.stopPropagation();
      var selector = button.closest('quantity-selector');
      var input = selector ? selector.querySelector('input[data-line-key]') : null;
      if (!input) {
        return;
      }
      var current = parseInt(input.value, 10) || 1;
      var isMinus = button === selector.querySelector('button:first-of-type');
      var next = clampQuantity(input, Math.max(1, current + (isMinus ? -1 : 1)));
      if (next !== current) {
        stageQuantity(selector, input, next);
      }
      return;
    }
    var remove = target.closest('#cart-drawer line-item-quantity a[href*="/cart/change"]');
    if (remove) {
      var holder = remove.closest('line-item-quantity');
      var removeSelector = holder ? holder.querySelector('quantity-selector') : null;
      var removeInput = removeSelector ? removeSelector.querySelector('input[data-line-key]') : null;
      if (!removeInput) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      var removingLine = holder.closest('line-item');
      if (removingLine) {
        removingLine.setAttribute('data-oc-removing', '');
      }
      stageQuantity(removeSelector, removeInput, 0);
    }
  }, true);

  /* Checkout: one submission at a time. Impact's custom-button shows its loader on submit; repeat clicks are ignored. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('buy-buttons') || !form.closest('#cart-drawer')) {
      return;
    }
    if (form.hasAttribute('data-oc-submitting')) {
      event.preventDefault();
      return;
    }
    form.setAttribute('data-oc-submitting', '');
    var submit = form.querySelector('button[type="submit"]');
    if (submit) {
      submit.setAttribute('aria-busy', 'true');
    }
    var cardCheckout = form.getAttribute('data-oc-card-checkout');
    if (cardCheckout) {
      event.preventDefault();
      window.location.href = cardCheckout;
      return;
    }
    setTimeout(function () {
      form.removeAttribute('data-oc-submitting');
      if (submit) {
        submit.removeAttribute('aria-busy');
      }
    }, 20000);
  }, true);
  window.addEventListener('pageshow', function () {
    document.querySelectorAll('#cart-drawer form[data-oc-submitting]').forEach(function (form) {
      form.removeAttribute('data-oc-submitting');
    });
  });

  /* Typed quantities (and arrow keys) go through the same path. */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-line-key') || !input.closest('#cart-drawer .quantity-selector--line')) {
      return;
    }
    event.stopPropagation();
    var selector = input.closest('quantity-selector');
    var typed = parseInt(input.value, 10);
    var next = clampQuantity(input, isNaN(typed) ? 1 : typed);
    stageQuantity(selector, input, next);
  }, true);

  /* ---------------------------------------------------------------- Cult Card baskets are one-time */

  var cardFixBusy = false;

  /** The drawer lists subscription oat lines sitting next to the card; move them to one-time, then redraw. */
  async function fixCardCart() {
    var totals = document.querySelector('#cart-drawer [data-oc-totals][data-oc-card-fix]');
    if (!totals || cardFixBusy) {
      return;
    }
    var lines;
    try {
      lines = JSON.parse(totals.getAttribute('data-oc-card-fix'));
    } catch (error) {
      return;
    }
    if (!lines || !lines.length) {
      return;
    }
    cardFixBusy = true;
    try {
      var cart = null;
      for (var i = 0; i < lines.length; i++) {
        cart = await change('cart/change.js', { id: lines[i].key, quantity: lines[i].quantity, selling_plan: '' });
      }
      totals.removeAttribute('data-oc-card-fix');
      publish(cart);
    } catch (error) {
      console.warn('[cart-upsells] card basket fix failed', error);
    } finally {
      cardFixBusy = false;
    }
  }

  /** Card holders see card prices in the drawer even though Shopify only applies their discount at checkout. */
  function applyCardPrices() {
    if (document.querySelector('#cart-drawer [data-oc-totals][data-oc-holder]')) {
      renderTotals();
    }
  }

  var dupeBusy = false;
  var dupeRemoved = false;

  /** A card holder with another Cult Card in the basket: take that line out, their card is already active. */
  async function removeDuplicateCard() {
    var totals = document.querySelector('#cart-drawer [data-oc-totals][data-oc-holder][data-oc-card-dupe]');
    if (!totals || dupeBusy) {
      return;
    }
    var key = totals.getAttribute('data-oc-card-dupe');
    if (!key) {
      return;
    }
    dupeBusy = true;
    try {
      var cart = await change('cart/change.js', { id: key, quantity: 0 });
      totals.removeAttribute('data-oc-card-dupe');
      dupeRemoved = true;
      publish(cart);
    } catch (error) {
      console.warn('[cart-upsells] duplicate card removal failed', error);
    } finally {
      dupeBusy = false;
    }
  }

  function afterDrawerRender() {
    if (document.querySelector('#cart-drawer [data-oc-totals][data-oc-holder][data-oc-card-dupe]')) {
      removeDuplicateCard();
      return;
    }
    if (dupeRemoved) {
      var note = document.querySelector('#cart-drawer [data-oc-dupe-note]');
      if (note) {
        note.hidden = false;
      }
    }
    fixCardCart();
    applyCardPrices();
  }

  document.addEventListener('cart:change', function (event) {
    var delay = event.detail && (event.detail.ocSource || event.detail.baseEvent === 'variant:add') ? 150 : 1400;
    setTimeout(afterDrawerRender, delay);
  });
  document.addEventListener('cart:refresh', function () {
    setTimeout(afterDrawerRender, 1200);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', afterDrawerRender);
  } else {
    afterDrawerRender();
  }

  /* Adds from the shop page that stack on top of an existing box change the tier: re-tier once Impact has redrawn. */
  var retierTimer = null;
  document.addEventListener('cart:change', function (event) {
    if (event.detail && event.detail.ocSource) {
      return;
    }
    clearTimeout(retierTimer);
    var delay = event.detail && event.detail.baseEvent === 'variant:add' ? 400 : 1500;
    retierTimer = setTimeout(async function () {
      var latest = await retier(event.detail && event.detail.cart && event.detail.cart.items ? event.detail.cart : null);
      if (latest) {
        publish(latest);
      }
      maybeRite(latest || (event.detail && event.detail.cart));
    }, delay);
  });

  /* ---------------------------------------------------------------- cult popup for big boxes */

  var riteShown = false;
  try {
    riteShown = window.sessionStorage.getItem('oc-rite') === '1';
  } catch (error) {
    /* storage unavailable */
  }

  async function maybeRite(cart) {
    if (riteShown) {
      return;
    }
    var holder = document.querySelector('[data-oc-rite]');
    if (!holder) {
      return;
    }
    var config;
    try {
      config = JSON.parse(holder.getAttribute('data-oc-rite'));
    } catch (error) {
      return;
    }
    var maps = planMaps();
    if (!cart || !cart.items) {
      try {
        cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      } catch (error) {
        return;
      }
    }
    var packs = (cart.items || []).filter(function (item) { return maps[String(item.variant_id)]; }).reduce(function (sum, item) { return sum + packsOf(item); }, 0);
    if (packs < Number(config.min || 5)) {
      return;
    }
    riteShown = true;
    try {
      window.sessionStorage.setItem('oc-rite', '1');
    } catch (error) {
      /* storage unavailable */
    }
    var rite = document.createElement('div');
    rite.className = 'oc-rite';
    rite.setAttribute('role', 'status');
    rite.innerHTML = '<span class="oc-rite__halo" aria-hidden="true"></span><img class="oc-rite__img" src="' + config.img + '" width="170" height="170" alt=""><p class="oc-rite__text"></p>';
    rite.querySelector('.oc-rite__text').textContent = config.text || '';
    var close = function () {
      if (!rite.parentNode) {
        return;
      }
      rite.classList.add('is-leaving');
      setTimeout(function () { rite.remove(); }, 320);
    };
    rite.addEventListener('click', close);
    document.body.appendChild(rite);
    setTimeout(close, 3200);
  }

  /**
   * Header cart bubble: Impact's <cart-count> fades itself in and out, but that fade stalls while the
   * drawer is open. Mirror the count into a data-empty attribute so CSS owns the empty state instead.
   */
  function stirSpoon(link) {
    if (!link) return;
    link.classList.remove('is-stirring');
    void link.offsetWidth;
    link.classList.add('is-stirring');
    var done = function () { link.classList.remove('is-stirring'); };
    link.addEventListener('animationend', done, { once: true });
    setTimeout(done, 1000);
  }

  function watchHeaderCount(el) {
    var prev = parseInt(el.textContent, 10) || 0;
    var sync = function () {
      var count = parseInt(el.textContent, 10) || 0;
      if (count > prev) stirSpoon(el.closest('.header__cart'));
      prev = count;
      if (count > 0) {
        el.removeAttribute('data-empty');
        if (el.getAnimations) {
          el.getAnimations().forEach(function (animation) { animation.cancel(); });
        }
        el.style.opacity = '';
      } else {
        el.setAttribute('data-empty', '');
      }
    };
    new MutationObserver(sync).observe(el, { childList: true, characterData: true, subtree: true });
    sync();
  }
  document.querySelectorAll('cart-count.header__cart-count').forEach(watchHeaderCount);
})();
